// Wide (50% page) fare-rules drawer. Two phases of info:
//
//   • Trip detail block — sourced from the FlightOption we already have
//     (no extra API call needed). One .fr-segment per segment, with a
//     .fr-layover banner showing layover duration between segments.
//
//   • Fare rules — populated from POST /v1/flights/fare-rules/. TravClan
//     returns rule_text as HTML, so we render via dangerouslySetInnerHTML.
//
// Foot: live held-for timer + inline Continue button so the user doesn't
// need to close the drawer to advance.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { CountdownPill } from "./HoldTimer";
import { SkeletonRuleBlocks } from "./Skeletons";
import { AirlineLogo } from "./AirlineLogo";
import { fmtCabin } from "../lib/labels";
import type { FareRulesResponse, FlightOption } from "../lib/types";

interface Props {
  trace_id: string;
  /** Full FlightOption(s) the user has selected. For one-way / domestic-RT
   *  this is a single item; intl-RT passes [outbound, inbound] so the drawer
   *  can show all legs in one view. */
  options: FlightOption[];
  /** Result_index used to fetch the fare-rules text (typically the bookable
   *  rI — for intl-RT that's the inbound_option's rI). */
  result_index: string;
  currency: string;
  onClose: () => void;
  /** Called when the user clicks Continue inside the drawer — same effect as
   *  the sticky-bar Continue button outside. */
  onContinue: () => void;
  continueDisabled?: boolean;
  continueLoading?: boolean;
  continueLabel?: string;
}

export function FareRulesDrawer(props: Props) {
  const q = useQuery({
    queryKey: ["fareRules", props.trace_id, props.result_index],
    queryFn: () =>
      api.post<FareRulesResponse>("/v1/flights/fare-rules/", {
        trace_id: props.trace_id,
        result_index: props.result_index,
      }),
    staleTime: 5 * 60_000,
  });

  // Live countdown for the fare-hold expiry. Pinned to absolute epoch so it
  // doesn't drift as the user scrolls / interacts.
  const targetMs = useMemo(() => {
    if (!q.data) return null;
    if (q.data.expires_at) {
      const t = new Date(q.data.expires_at).getTime();
      if (!Number.isNaN(t)) return t;
    }
    if (q.data.remaining_seconds) return Date.now() + q.data.remaining_seconds * 1000;
    return null;
  }, [q.data]);

  // Aggregate trip stats from the FlightOptions for the header.
  const trip = useMemo(() => summarise(props.options), [props.options]);
  // On a COUPLED round trip both legs come from one offer: they share a
  // result_index and each carries the same *combined* fare, so summing them
  // would show double the price. Total one fare per distinct result_index.
  // (An independent round trip has two distinct indices and still sums.)
  const totalFare = useMemo(() => {
    const seen = new Set<string>();
    return props.options.reduce((sum, o) => {
      if (seen.has(o.result_index)) return sum;
      seen.add(o.result_index);
      return sum + o.pricing.final_fare;
    }, 0);
  }, [props.options]);
  const headAirline = props.options[0]?.segments[0]?.airline;

  return (
    <div className="drawer-overlay" onClick={props.onClose}>
      <aside className="drawer fare-rules-wide" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <h2>Flight <span className="serif">details</span> &amp; fare rules</h2>
            <div className="sub">
              {trip.routeLabel} · {trip.legCount} {trip.legCount === 1 ? "leg" : "legs"} · {fmtDuration(trip.totalDuration)} total
            </div>
          </div>
          <button type="button" className="drawer-close" onClick={props.onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Top-level summary chip */}
        <div className="fr-summary">
          <AirlineLogo code={headAirline?.code || ""} name={headAirline?.name} />
          <div>
            <div className="airline-name">{headAirline?.name || headAirline?.code}</div>
            <div className="meta">
              <span className={`tag ${props.options.every((o) => o.is_refundable) ? "refundable" : "non-refundable"}`}>
                {props.options.every((o) => o.is_refundable) ? "Refundable" : "Non-refundable"}
              </span>
              <span>{trip.routeLabel}</span>
              <span>{trip.startDateLabel}</span>
            </div>
          </div>
          <div className="total" style={{ textAlign: "right" }}>
            <div className="amt">{fmtMoney(totalFare, props.currency)}</div>
            <div className="sub">all incl.</div>
          </div>
        </div>

        {/* Per-leg segment detail */}
        {props.options.map((opt, optIdx) => {
          const dir = optIdx === 0 ? (props.options.length > 1 ? "Outbound" : "Trip") : "Return";
          return (
            <div key={opt.result_index + "-" + optIdx}>
              <div className="fr-section-head">{dir} · {fmtPrettyDate(opt.segments[0]?.origin.departure_time)}</div>

              {/* Route strip */}
              <div className="fr-route-strip" style={{ background: "var(--bg-2)", borderRadius: 12, marginBottom: 14 }}>
                <div>
                  <div className="time">{fmtTime(opt.segments[0]?.origin.departure_time)}</div>
                  <div className="sub">{opt.segments[0]?.origin.city_name || opt.segments[0]?.origin.airport_code} ({opt.segments[0]?.origin.airport_code})</div>
                </div>
                <div className="mid">
                  <div className="dur">{fmtDuration(legDur(opt))}</div>
                  <div className="stops">{opt.stop_count === 0 ? "Non-stop" : `${opt.stop_count} stop${opt.stop_count > 1 ? "s" : ""}`}</div>
                </div>
                <div className="right">
                  <div className="time">{fmtTime(opt.segments[opt.segments.length - 1]?.destination.arrival_time)}</div>
                  <div className="sub">{opt.segments[opt.segments.length - 1]?.destination.city_name || opt.segments[opt.segments.length - 1]?.destination.airport_code} ({opt.segments[opt.segments.length - 1]?.destination.airport_code})</div>
                </div>
              </div>

              {opt.segments.map((s, si) => {
                const prev = si > 0 ? opt.segments[si - 1] : null;
                return (
                  <div key={si}>
                    {prev && (
                      <div className="fr-layover">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                        <span><span className="lbl">Change of planes</span> · {fmtDuration(layoverMinutes(prev, s))} layover in {prev.destination.city_name || prev.destination.airport_code} ({prev.destination.airport_code})</span>
                      </div>
                    )}

                    <div className="fr-segment">
                      <div className="fr-segment-head">
                        <AirlineLogo code={s.airline.code} name={s.airline.name} />
                        <div className="id">
                          <div className="name">{s.airline.name || s.airline.code}</div>
                          <div className="num">{s.airline.code} {s.airline.flight_number}</div>
                        </div>
                      </div>

                      <div className="fr-segment-route">
                        <div>
                          <div className="city">{s.origin.city_name || s.origin.airport_code}</div>
                          <div className="ap">{s.origin.airport_code}{s.origin.airport_name ? `, ${s.origin.airport_name}` : ""}{s.origin.terminal ? ` · ${s.origin.terminal}` : ""}</div>
                        </div>
                        <div className="dur"><span className="arrow">→</span> {fmtDuration(s.duration_minutes)}</div>
                        <div>
                          <div className="city">{s.destination.city_name || s.destination.airport_code}</div>
                          <div className="ap">{s.destination.airport_code}{s.destination.airport_name ? `, ${s.destination.airport_name}` : ""}{s.destination.terminal ? ` · ${s.destination.terminal}` : ""}</div>
                        </div>
                      </div>

                      <div className="fr-segment-meta">
                        <div className="pair"><div className="lbl">Departure</div><div className="val">{fmtTime(s.origin.departure_time)} · {fmtPrettyDate(s.origin.departure_time)}</div></div>
                        <div className="pair"><div className="lbl">Arrival</div><div className="val">{fmtTime(s.destination.arrival_time)} · {fmtPrettyDate(s.destination.arrival_time)}</div></div>
                        <div className="pair"><div className="lbl">Cabin class</div><div className="val">{fmtCabin(s.cabin_class)}</div></div>
                        <div className="pair"><div className="lbl">Baggage</div><div className="val">{s.baggage_allowance || "—"}{s.cabin_baggage_allowance ? ` + ${s.cabin_baggage_allowance} cabin` : ""}</div></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Fare rules text */}
        <div className="fr-section-head">Fare rules</div>
        {q.isLoading && <SkeletonRuleBlocks />}
        {q.error && (
          <div className="rule-block">
            <div className="rule-section"><p style={{ color: "var(--red)" }}>Couldn't load rules: {(q.error as Error).message}</p></div>
          </div>
        )}
        {q.data?.rules.length === 0 && (
          <div className="rule-block">
            <div className="rule-section"><p>The airline didn't return rule text for this fare.</p></div>
          </div>
        )}
        {q.data?.rules.map((rule, i) => (
          <div key={i} className="rule-block">
            <div className="rule-block-head">
              <div>
                <div className="name">{rule.airline}</div>
                <div className="route">{rule.origin} → {rule.destination}</div>
              </div>
            </div>
            <div className="rule-section rule-html" dangerouslySetInnerHTML={{ __html: rule.rule_text || "" }} />
          </div>
        ))}

        <div className="drawer-foot" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {targetMs != null && (
            <CountdownPill targetMs={targetMs} prefix="Fare held" hideWhenExpired />
          )}
          <button
            type="button"
            className={`btn btn-yellow btn-pill btn-full${props.continueLoading ? " loading" : ""}`}
            disabled={props.continueDisabled || props.continueLoading}
            onClick={props.onContinue}
          >
            {props.continueLabel || "Continue with this fare"}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
          </button>
        </div>
      </aside>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function summarise(options: FlightOption[]) {
  if (!options.length) return { routeLabel: "", legCount: 0, totalDuration: 0, startDateLabel: "" };
  const first = options[0].segments[0];
  const last = options[options.length - 1].segments[options[options.length - 1].segments.length - 1];
  const routeLabel = options.length > 1 && last.destination.airport_code === first.origin.airport_code
    ? `${first.origin.airport_code} ⇄ ${options[0].segments[options[0].segments.length - 1].destination.airport_code}`
    : `${first.origin.airport_code} → ${last.destination.airport_code}`;
  const totalDuration = options.reduce((s, o) => s + legDur(o), 0);
  const legCount = options.reduce((s, o) => s + o.segments.length, 0);
  return {
    routeLabel,
    legCount,
    totalDuration,
    startDateLabel: fmtPrettyDate(first.origin.departure_time),
  };
}

function legDur(o: FlightOption): number {
  return o.segments.reduce((s, x) => s + (x.duration_minutes || 0) + (x.ground_time_minutes || 0), 0);
}
function layoverMinutes(prev: { destination: { arrival_time?: string } }, cur: { origin: { departure_time?: string } }): number {
  const a = new Date(prev.destination.arrival_time || 0).getTime();
  const d = new Date(cur.origin.departure_time || 0).getTime();
  if (!a || !d) return 0;
  return Math.max(0, Math.round((d - a) / 60000));
}
function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDuration(mins: number): string {
  if (!Number.isFinite(mins)) return "—";
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtPrettyDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}
function fmtMoney(n: number, currency: string): string {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${Math.round(n).toLocaleString("en-IN")}`; }
}
